export type LegalConsentFieldKey =
  | 'acceptAgb'
  | 'acceptPrivacy'
  | 'acceptGuideline'
  | 'contactViaEmail'
  | 'contactViaWhatsapp'
  | 'acceptConfirmData'

export type ProofTextContext = {
  t: (key: string, params?: Record<string, unknown>) => string
  legalCompanyName?: string | null
  readablePlatformName?: string | null
  /** Gates the `termsOf` fragment below, mirroring the `v-if="publicTOCandLicenceUrl"` in ChatLegalConsents.vue/OnboardingLegalConsents.vue. */
  publicTOCandLicenceUrl?: string | null
}

export type LegalConsentField = {
  key: LegalConsentFieldKey
  /** Must match a "Legal Notice" identifier configured in the iubenda dashboard. */
  legalNoticeIdentifier: string
  /** The exact text shown for this checkbox, given current i18n/branding context. */
  resolveProofText: (ctx: ProofTextContext) => string
}

export const legalConsentsFields: LegalConsentField[] = [
  {
    key: 'acceptAgb',
    legalNoticeIdentifier: 'onboarding_agb',
    resolveProofText: ({ t, legalCompanyName, publicTOCandLicenceUrl }) =>
      [
        t('onboarding.submit.acceptTerms'),
        // Only shown to the user (and thus only part of the proof) when a TOC/licence URL exists.
        publicTOCandLicenceUrl
          ? t('onboarding.submit.termsOf', { company: legalCompanyName })
          : null,
      ]
        .filter((part) => typeof part === 'string' && part.trim())
        .join(' '),
  },
  {
    key: 'acceptPrivacy',
    legalNoticeIdentifier: 'onboarding_privacy',
    resolveProofText: ({ t, legalCompanyName }) =>
      [
        t('onboarding.submit.acceptPrivacy'),
        t('onboarding.submit.privacyPolicyOf', { company: legalCompanyName }),
        t('onboarding.submit.acceptPrivacyEnd'),
      ]
        .filter((part) => typeof part === 'string' && part.trim())
        .join(' '),
  },
  {
    key: 'acceptGuideline',
    legalNoticeIdentifier: 'onboarding_guideline',
    resolveProofText: ({ t, readablePlatformName }) =>
      [
        t('onboarding.submit.acceptGuideline'),
        t('onboarding.submit.guidelineFor', { platform: readablePlatformName }),
        t('onboarding.submit.received'),
      ]
        .filter((part) => typeof part === 'string' && part.trim())
        .join(' '),
  },
  {
    key: 'contactViaEmail',
    legalNoticeIdentifier: 'onboarding_contact_email',
    resolveProofText: ({ t }) => t('onboarding.submit.contactViaEmail'),
  },
  {
    key: 'contactViaWhatsapp',
    legalNoticeIdentifier: 'onboarding_contact_whatsapp',
    resolveProofText: ({ t }) => t('onboarding.submit.contactViaWhatsapp'),
  },
  {
    key: 'acceptConfirmData',
    legalNoticeIdentifier: 'onboarding_confirm_data',
    resolveProofText: ({ t }) => t('onboarding.submit.confirmAllData'),
  },
]
